import { SyntheticEvent } from 'react';
import BootstrapForm from 'react-bootstrap/Form';

import { createTestId, DynamicTestId } from 'common/element';

type SelectListProps = {
  options:    Array<string>;
  values?:    Array<string>;
  onChange?:  (fields: Array<string>) => void;
  selected?:  string | string[];
  multiple?:  boolean;
  className?: string;
  testId?:    DynamicTestId;
};

export type Callback = (ev?: SyntheticEvent<Element, Event>) => void;

/**
 * This component wraps a form SELECT. The main feature it provides is wrapping up selected options into a list
 * which is considered to be the component's value. The list of options is treated as a set, without duplicates.
 *
 * @prop options      list of options
 * @prop values       list of values, parallel to options
 * @prop onChange     function to call when value changes
 * @prop selected     list of selected option values
 * @prop multiple     if true, multiple selection is allowed
 * @prop className    class name for the SELECT
 * @prop testId       unique string for testing
 */
function SelectList({
  options,
  values,
  onChange,
  selected = [],
  multiple = false,
  className,
  testId,
}: SelectListProps): JSX.Element {

  const optionEls = options.map((option, index) => {
    const optionTestId = testId ? createTestId(testId, 'item', index) : undefined;
    const value = values ? values[index] : option;
    return (
      <option value={value} key={value} data-test-id={optionTestId}>
        {option}
      </option>
    );
  });

  const handleChange: Callback = (ev) => {
    // options is array-like HTMLOptionsCollection
    const selectedOptions = [];
    if (ev) {
      const event = ev.target as HTMLSelectElement;
      for (let i = 0; i < event.options.length; i += 1) {
        if (event.options[i].selected) {
          selectedOptions.push(event.options[i]);
        }
      }
      if (onChange) {
        onChange(selectedOptions.map((option) => option.value));
      }
    }
  };

  return (
    <BootstrapForm.Control
      as="select"
      multiple={multiple}
      value={selected}
      onChange={onChange ? handleChange : undefined}
      className={className || undefined}
      data-test-id={testId}
    >
      {optionEls}
    </BootstrapForm.Control>
  );
}

export default SelectList;
